import { useState, type FormEvent } from "react";
import { whatsappLink } from "@/constants/site";
import { domainTlds } from "@/mocks/dominios";

interface DomainResult {
  tld: string;
  price: string;
  available: boolean;
}

export default function DomainSearch() {
  const [name, setName] = useState("");
  const [results, setResults] = useState<DomainResult[] | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const clean = name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

    if (!clean) {
      setResults(null);
      return;
    }

    const next = domainTlds.map((item, index) => ({
      tld: item.tld,
      price: item.price,
      available: (clean.length + index) % 3 !== 0,
    }));
    setResults(next);
  }

  return (
    <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line" aria-hidden="true"></i>
          </span>
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Digite o nome do seu domínio"
            aria-label="Nome do domínio"
            className="w-full rounded-md border border-background-300 bg-background-50 py-3.5 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-search-eye-line text-lg" aria-hidden="true"></i>
          Verificar disponibilidade
        </button>
      </form>

      {results ? (
        <div className="mt-6">
          <p className="mb-3 text-xs uppercase tracking-wide text-foreground-500">
            Resultados para{" "}
            <span className="font-semibold text-foreground-800">
              {name.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}
            </span>
          </p>
          <ul className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200">
            {results.map((result) => {
              const domain = `${name
                .trim()
                .toLowerCase()
                .replace(/\s+/g, "-")
                .replace(/[^a-z0-9-]/g, "")}${result.tld}`;
              return (
                <li
                  key={result.tld}
                  className="flex flex-col items-start justify-between gap-3 bg-background-50 px-4 py-4 sm:flex-row sm:items-center"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex h-9 w-9 items-center justify-center rounded-full ${
                        result.available
                          ? "bg-secondary-100 text-secondary-700"
                          : "bg-background-200 text-foreground-400"
                      }`}
                    >
                      <i
                        className={result.available ? "ri-check-line" : "ri-close-line"}
                        aria-hidden="true"
                      ></i>
                    </span>
                    <div>
                      <p className="font-heading text-sm font-semibold text-foreground-950">
                        {domain}
                      </p>
                      <p className="text-xs text-foreground-500">
                        {result.available ? "Disponível para registro" : "Indisponível no momento"}
                      </p>
                    </div>
                  </div>

                  {result.available ? (
                    <div className="flex w-full items-center justify-between gap-4 sm:w-auto">
                      <span className="text-sm font-semibold text-foreground-950">
                        R$ {result.price}
                        <span className="text-xs font-normal text-foreground-500"> /ano</span>
                      </span>
                      <a
                        href={whatsappLink(`Ola! Quero registrar o dominio ${domain} na NXHOST.`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2.5 text-xs font-semibold text-background-50 transition-colors hover:bg-foreground-800"
                      >
                        Registrar
                      </a>
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-xs font-medium text-foreground-500">
                      Buscar outro nome
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="mt-4 text-xs text-foreground-500">
            A disponibilidade final é confirmada pela nossa equipe no momento do registro.
          </p>
        </div>
      ) : null}
    </div>
  );
}